// Mutiny -- two separate mechanics that both end with an ejected captain:
//
// 1. **Provisions mutiny (16.62)** on the captain's OWN ship: a real
//    20-real-clock-minute grace period with a warning every 5 minutes,
//    reversible right up to the deadline by resupplying.
// 2. **Loaner hull mutiny (16.58/16.73)** on a poster-provided loaner:
//    driven by hull percentage instead of the clock, and explicitly
//    IRREVERSIBLE once it fires -- see the loaner section at the bottom
//    of this file.
//
// They never overlap: runMutinyCheck() ignores a loaner (it carries no
// Provisions of its own), and the loaner check ignores everything else.
import { effectiveNow } from "./gameClock";
import type { AppState } from "./ui/state";

const WARNING_INTERVAL_MS = 5 * 60 * 1000;
const EJECT_AT_WARNING_INDEX = 4; // 4 * 5min = 20min

// Recomputed fresh, same "derive, don't cache" convention as
// missionChecks.ts/shipUpkeep.ts. Returns true if anything changed (a
// warning fired or the captain was ejected) so callers know to save.
export function runMutinyCheck(state: AppState, nowMs: number): boolean {
  if (!state.currentShip || state.isLoanerShip) {
    // A loaner ship carries no Provisions of its own to run out of
    // (16.63) -- mutiny only ever applies to the captain's own owned/
    // hired ship. Shipless (Escape Pod) captains have nothing to mutiny
    // on either.
    return false;
  }

  if (state.provisionsOnBoard > 0) {
    if (state.provisionsDepletedAt !== null) {
      // Resupplied before the deadline -- cancels the countdown entirely
      // (Don's direction: buying more Provisions in time averts mutiny).
      state.provisionsDepletedAt = null;
      state.mutinyWarningsIssued = 0;
      return true;
    }
    return false;
  }

  const now = effectiveNow(state.clock, nowMs);

  if (state.provisionsDepletedAt === null) {
    state.provisionsDepletedAt = now;
    state.mutinyWarningsIssued = 0;
    state.lastEvent = "Provisions have run out -- the crew is growing uneasy.";
    return true;
  }

  const elapsedMs = now - state.provisionsDepletedAt;
  const warningIndex = Math.min(EJECT_AT_WARNING_INDEX, Math.floor(elapsedMs / WARNING_INTERVAL_MS));

  if (warningIndex >= EJECT_AT_WARNING_INDEX) {
    ejectCaptain(state);
    return true;
  }

  if (warningIndex > state.mutinyWarningsIssued) {
    state.mutinyWarningsIssued = warningIndex;
    const minutesLeft = 20 - warningIndex * 5;
    state.lastEvent = `The crew warns they'll mutiny without Provisions -- ${minutesLeft} minutes left to resupply.`;
    return true;
  }

  return false;
}

// The ship and crew are not lost -- they simply stay wherever the ship
// was last docked (FleetShip.dockedAtKey, kept up to date by every
// real Dock action, expeditionScreen.ts's tryDock). The captain becomes
// shipless and enters the Escape Pod (16.62).
function ejectCaptain(state: AppState): void {
  const fleetEntry = state.fleet.find((f) => f.id === state.currentShipInstanceId);
  state.lastEvent = fleetEntry
    ? `Mutiny! The crew ejects you and returns ${fleetEntry.ship.name} to port.`
    : "Mutiny! The crew ejects you.";
  state.currentShip = null;
  state.currentShipInstanceId = null;
  state.isLoanerShip = false;
  state.inEscapePod = true;
  state.provisionsOnBoard = 0;
  state.provisionsCapacity = 0;
  state.fuelOnBoard = 0;
  state.fuelCapacity = 0;
  state.provisionsDepletedAt = null;
  state.mutinyWarningsIssued = 0;
}

// ---------------------------------------------------------------------
// Loaner hull mutiny (16.58's 25% threshold, mechanic locked in 16.73)
// ---------------------------------------------------------------------
//
// A loaner's crew belongs to the mission poster, not the captain -- they
// never agreed to fight someone else's war in someone else's hull. So
// unlike the Provisions mutiny above, this one is:
//   - driven by hull percentage, not the clock;
//   - **irreversible** -- repairing back above the threshold does NOT
//     call the crew off, which is why the UI has to telegraph the point
//     of no return BEFORE it's crossed (see LOANER_HULL_STRIPE below);
//   - free. No standing penalty, no credit penalty (16.73): the crew is
//     safe, the ship is safe, and the captain has already paid in wasted
//     time and a forfeited mission.
//
// The thresholds deliberately reuse the hull-bar colour bands the HUD
// already uses (Section 10: 0.6 caution, 0.3 critical) rather than
// inventing a parallel set of numbers -- the warnings land exactly where
// the bar visibly changes state.
export const LOANER_HULL_WARN_CAUTION = 0.6; // "we can't take much more of this"
export const LOANER_HULL_STRIPE = 0.3; // striped bar: point of no return is close
export const LOANER_HULL_MUTINY = 0.25; // 16.58's locked threshold

// Escalating warning tiers, derived purely from current hull fraction.
// 0 = untouched, 1 = has taken any damage at all, 2 = at/below the
// caution band, 3 = mutiny. Callers track the highest tier already shown
// so each line fires once as the hull falls, and never re-fires on the
// way back up.
export function loanerHullWarningTier(hullFraction: number): 0 | 1 | 2 | 3 {
  if (hullFraction <= LOANER_HULL_MUTINY) return 3;
  if (hullFraction <= LOANER_HULL_WARN_CAUTION) return 2;
  if (hullFraction < 1) return 1;
  return 0;
}

export function loanerWarningMessage(tier: 1 | 2): string {
  return tier === 1
    ? "The loaner's crew protest -- they didn't sign up for this."
    : "The loaner's crew are close to breaking -- they can't take much more of this.";
}

// True once the bar should render striped: the captain is inside the
// last band before the crew walks, and needs to see it coming.
export function loanerHullAtRisk(hullFraction: number): boolean {
  return hullFraction <= LOANER_HULL_STRIPE;
}

// The crew fly the loaner out from under the captain and warp it home,
// leaving them podded exactly where they were (16.73). Nothing is
// destroyed and nobody dies -- the ship and its crew are simply gone, and
// the captain keeps their current System/Location. Pod travel is safe by
// design (16.72), so being left here is a setback, not a death sentence.
export function runLoanerHullMutiny(state: AppState, hullFraction: number): boolean {
  if (!state.isLoanerShip || !state.currentShip) return false;
  if (loanerHullWarningTier(hullFraction) < 3) return false;

  state.lastEvent =
    "Mutiny! The loaner's crew refuse to take another hit -- they warp the ship home and leave you in your pod.";
  state.currentShip = null;
  state.currentShipInstanceId = null;
  state.isLoanerShip = false;
  state.inEscapePod = true;
  state.fuelOnBoard = 0;
  state.fuelCapacity = 0;
  state.provisionsOnBoard = 0;
  state.provisionsCapacity = 0;
  return true;
}
